import { Form, Button, Input, Select, Spin } from 'antd';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import React, { useCallback, useEffect, useState } from 'react';

import { MdOutlineKeyboardArrowLeft } from 'react-icons/md';

import '../../../css/UserAdmin/Global.css';

import Footer from '../Footer';
import Header from '../NewHeader';
import SideNav from '../SideNav';

const CreateAcademicYear = () => {
    const navigate = useNavigate();
    const [isSideNavOpen, setIsSideNavOpen] = useState(false);
    const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 768);
    const location = useLocation();
    const [form] = Form.useForm();
    const { id } = useParams();
    const [loading, setLoading] = useState(false);
    const [initialDataLoading, setInitialDataLoading] = useState(false);

    // Determine mode based on URL and presence of ID
    const isViewMode = location.pathname.includes('/view/');
    const isEditMode = !!id && !isViewMode; // ID exists, and it's not view mode
    const isCreateMode = !id; // No ID means create mode

    const toggleSideNav = useCallback(() => setIsSideNavOpen(prev => !prev), []);

    useEffect(() => {
        const handleResize = () => setIsSmallScreen(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleBack = () => navigate('/admin/manage-enrollment-period');

    useEffect(() => {
        if (id) { // Fetch data if an ID is present (for Edit or View mode)
            setInitialDataLoading(true); // Use separate loading for initial data
            setLoading(true); // Keep general loading true for disabling fields during fetch
            const fetchYearData = async () => {
                try {
                    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/years/${id}`);
                    const data = await response.json();
                    if (response.ok && data.data) {
                        form.setFieldsValue({
                            Year: data.data.Year,
                            status: data.data.status,
                        });
                    } else {
                        toast.error(data.message || 'Failed to load academic year data.');
                        navigate('/admin/manage-enrollment-period');
                    }
                } catch (error) {
                    console.error('Network error fetching academic year:', error);
                    toast.error('Network error. Could not fetch academic year data.');
                    navigate('/admin/manage-enrollment-period');
                } finally {
                    setInitialDataLoading(false);
                    setLoading(false);
                }
            };
            fetchYearData();
        }
    }, [id, form, navigate]); // Rerun if id, form, or navigate changes

    const handleSubmit = async (values) => {
        if (isViewMode) return; // Should not be callable in view mode

        setLoading(true);
        const url = isEditMode
            ? `${process.env.REACT_APP_API_URL}/api/admin/years/${id}` // 'id' should be the ID of the academic year being edited
            : `${process.env.REACT_APP_API_URL}/api/admin/years/`;

        const method = isEditMode ? 'PATCH' : 'POST';

        try {
            const response = await fetch(url, {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    Year: values.Year, // Make sure 'values' contains 'Year'
                    status: values.status, // Make sure 'values' contains 'status'
                }),
            });

            const data = await response.json();

            if (response.ok) {
                toast.success(`Academic year ${isEditMode ? 'updated' : 'created'} successfully.`);

                // Get user info from localStorage
                const fullName = localStorage.getItem('fullName');
                const role = localStorage.getItem('role');
                const userID = localStorage.getItem('userID');

                const logAction = isEditMode ? 'Update' : 'Create'; // Determine action based on isEditMode

                let logDetail = `${logAction}d academic year [${values.Year}]`;
                if (values.status) {
                    logDetail += ` with status [${values.status}]`;
                }
                logDetail += `.`;

                const logData = {
                    userID: userID || 'System',
                    accountName: fullName || 'Unknown User',
                    role: role || 'Unknown Role',
                    action: logAction,
                    detail: logDetail,
                };

                fetch(`${process.env.REACT_APP_API_URL}/api/admin/system-logs`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(logData)
                })
                    .then(logResponse => {
                        if (!logResponse.ok) {
                            logResponse.json().then(errData => {
                                console.error('System log API returned an error:', errData);
                            }).catch(() => {
                                console.error('System log API returned an error and failed to parse JSON response.');
                            })
                        }
                        return logResponse.json();
                    })
                    .then(logResultData => { // Renamed to avoid conflict with outer 'data'
                        console.log('System log recorded:', logResultData);
                    })
                    .catch(error => {
                        console.error('Failed to record system log:', error);
                    });
                navigate('/admin/manage-enrollment-period');
            } else {
                console.error(`Error ${isEditMode ? 'updating' : 'creating'} academic year:`, data.error || data.message);
                toast.error(data.error || data.message || 'Something went wrong.');
            }
        } catch (error) {
            console.error('Network error:', error);
            toast.error('Network error. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        if (isViewMode) return; // Prevent changes in view mode

        let value = e.target.value;
        value = value.replace(/[^\d-]/g, '');

        if (value.length === 4 && !value.includes('-')) {
            // No auto-add
        } else if (value.length > 4 && value.charAt(4) !== '-') {
            value = value.slice(0, 4) + '-' + value.slice(4);
        }

        if (value.length > 9) {
            value = value.slice(0, 9);
        }

        form.setFieldsValue({ Year: value });
    };

    let pageTitle = 'Create Academic Year';
    if (isEditMode) {
        pageTitle = 'Edit Academic Year';
    } else if (isViewMode) {
        pageTitle = 'View Academic Year';
    }

    return (
        <div className={`admin-page-container ${isSideNavOpen ? 'admin-sidenav-open' : ''}`}>
            <Header onMenuClick={toggleSideNav} isSideNavOpen={isSideNavOpen} />
            <div className="admin-content-wrapper">
                <div className="admin-sidenav-container">
                    <SideNav isOpen={isSideNavOpen} onClose={toggleSideNav} isSmallScreen={isSmallScreen} />
                </div>
                <main className="admin-main-content-area">
                    <div className="page-title">
                        <div className="arrows" onClick={handleBack}>
                            <MdOutlineKeyboardArrowLeft />
                        </div>
                        <p className="heading">{pageTitle}</p>
                    </div>

                    {initialDataLoading && <Spin tip="Loading data..." size="large" style={{ display: 'block', margin: '20px auto' }} />}

                    {!initialDataLoading && (
                        <Form
                            form={form}
                            layout="horizontal"
                            labelCol={{ xs: { span: 24 }, sm: { span: 6 } }}
                            wrapperCol={{ xs: { span: 24 }, sm: { span: 30 } }}
                            onFinish={handleSubmit}
                        >
                            <div className="container-columns">
                                <div className="column">
                                    <Form.Item
                                        label="Academic Year"
                                        name="Year"
                                        rules={isViewMode ? [] : [
                                            {
                                                required: true,
                                                message: 'Please input academic year!',
                                            },
                                            {
                                                pattern: /^\d{4}-\d{4}$/,
                                                message: 'Format must be YYYY-YYYY (e.g., 2023-2024)',
                                            },
                                            {
                                                validator: (_, value) => {
                                                    if (!value || !/^\d{4}-\d{4}$/.test(value)) return Promise.resolve();

                                                    const parts = value.split('-');
                                                    const startYear = parseInt(parts[0]);
                                                    const endYear = parseInt(parts[1]);

                                                    if (isNaN(startYear) || isNaN(endYear)) {
                                                        return Promise.reject(new Error('Years must be numbers.'));
                                                    }
                                                    if (endYear - startYear !== 1) {
                                                        return Promise.reject(new Error('End year must be exactly one year after start year.'));
                                                    }
                                                    const currentYear = new Date().getFullYear();
                                                    if (startYear < currentYear - 10) {
                                                        return Promise.reject(new Error('Academic year cannot be more than 10 years in the past.'));
                                                    }
                                                    if (startYear > currentYear + 5) {
                                                        return Promise.reject(new Error('Academic year cannot be more than 5 years in the future.'));
                                                    }
                                                    return Promise.resolve();
                                                },
                                            },
                                        ]}
                                    >
                                        <Input
                                            placeholder="Enter academic year (e.g., 2023-2024)"
                                            maxLength={9}
                                            onChange={handleInputChange}
                                            disabled={isViewMode || loading} // Disabled in view mode or general loading
                                            readOnly={isViewMode} // Explicitly make readOnly for view mode
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        label="Status"
                                        name="status"
                                        rules={isViewMode ? [] : [{ required: true, message: 'Please select a status!' }]}
                                    >
                                        <Select
                                            placeholder="Select a status"
                                            disabled={isViewMode || loading} // Disabled in view mode or general loading
                                        >
                                            <Select.Option value="Active">Active</Select.Option>
                                            <Select.Option value="Inactive">Inactive</Select.Option>
                                        </Select>
                                    </Form.Item>

                                    {!isViewMode && (
                                        <div className="buttons">
                                            <Button type="default" htmlType="button" onClick={handleBack} disabled={loading}>
                                                Cancel
                                            </Button>
                                            <Button
                                                type="primary"
                                                htmlType="submit"
                                                className={isCreateMode ? 'create-btn' : ''}
                                                loading={loading}
                                            >
                                                {isEditMode ? 'Update' : 'Save'}
                                            </Button>
                                        </div>
                                    )}
                                </div>
                                <div className="column"></div>
                            </div>
                        </Form>
                    )}
                </main>
            </div>
            <Footer />
        </div>
    );
};

export default CreateAcademicYear;